import * as XLSX from 'xlsx-js-style';
import { unzipSync, zipSync, strToU8 } from 'fflate';
import { loadLogoPngBytes } from './brandLogo';

/**
 * Writes a styled sheet to an .xlsx file and triggers the browser download.
 *
 * ── THE LAYOUT ────────────────────────────────────────────────────────────
 * Every spreadsheet this app produces follows the shape of the client's own
 * Billing.xlsx, so a file we generate sits beside one they sent without looking
 * like it came from somewhere else:
 *
 *     ┌──────────────────────────────────────────────┐
 *     │  TITLE                                       │  navy banner
 *     │  subtitle                                    │
 *     ├──────────────────────────────────────────────┤
 *     │  Label              value                    │  label / value pairs
 *     ├──────────────────────────────────────────────┤
 *     │  SECTION NAME                                │  full-width bar
 *     │  Col  │ Col  │ Col                           │  header band
 *     │  data │ data │ data                          │  bordered rows
 *     └──────────────────────────────────────────────┘
 *
 * ⚠️ THE LOGO CANNOT TRAVEL. The client's workbook has the CFT mark in its
 * banner; `xlsx-js-style` has no image API at all (nor does SheetJS community),
 * so embedding one would mean a second xlsx library in the bundle. The navy
 * banner and the brand navy carry the identity instead. The PDF invoice, which
 * is drawn with jsPDF, does have the logo.
 *
 * ── WHY THIS LIBRARY ──────────────────────────────────────────────────────
 * `xlsx-js-style` rather than the plain `xlsx` also in the project: SheetJS's
 * community build silently DROPS `cell.s` on write, so none of the above is
 * possible with it. Same API otherwise.
 *
 * @param title          banner heading
 * @param subtitle       second banner line, optional
 * @param rows           array of arrays; ragged rows are fine
 * @param sectionRows    row indexes drawn as a full-width section bar
 * @param headerRows     row indexes drawn as a table header band
 * @param labelRows      row indexes where column A is a label beside a value
 * @param highlightRows  label/value rows carrying a HEADLINE figure
 * @param totalRows      rows inside a table that total the rows above them
 * @param boldColumns    column indexes bolded in every plain data row
 * @param noteRows       row indexes drawn as a wrapped note block
 * @param numberFormats  { [columnIndex]: '$#,##0.00' } applied to data rows
 * @param fileName       e.g. "Acme-Oct-2026-Billing-Report.xlsx"
 * @param sheetName      worksheet tab name
 *
 * `boldRows` / `boldLabelRows` are accepted as the previous names for
 * `headerRows` / `labelRows`, so older call sites keep working.
 */

/* The client's workbook uses Office's stock blues. The banner takes CFT's own
   navy instead — a shade away from theirs, and the same colour the PDF invoice
   header already uses, so the two documents match each other. */
const NAVY = '2D3D5C';   // banner, section bars
const SECTION = '2E75B6'; // sub-section bars
const HEAD = 'DDEBF7';   // table header band, label cells
const VALUE = 'E2EFDA';  // highlighted figures
const NOTE_BG = 'F7F9FC';
const RULE = 'B4C6E7';
const INK = '1F3864';

const thin = { style: 'thin', color: { rgb: RULE } };
const boxed = { top: thin, bottom: thin, left: thin, right: thin };
// A total is ruled off from what it sums, the way an accountant would.
const medium = { style: 'medium', color: { rgb: NAVY } };
const totalled = { top: medium, bottom: thin, left: thin, right: thin };


/* ------------------------------------------------------------------------- *
 * The logo.
 *
 * ⚠️ NEITHER SheetJS NOR xlsx-js-style HAS AN IMAGE API. An .xlsx is a zip of
 * XML parts, so the mark is added by writing the workbook, opening the zip, and
 * putting the five parts a picture needs into it by hand:
 *
 *   xl/media/image1.png                     the bytes
 *   xl/drawings/drawing1.xml                where it sits and how big
 *   xl/drawings/_rels/drawing1.xml.rels     drawing -> media
 *   xl/worksheets/_rels/sheet1.xml.rels     sheet -> drawing
 *   [Content_Types].xml                     declares png and the drawing part
 *
 * plus a <drawing> element inside the sheet itself. Excel treats a workbook
 * with any one of those missing as corrupt, which is why this is all-or-nothing
 * and why a failure anywhere returns the untouched workbook instead.
 *
 * The alternative was ExcelJS, which does images natively and would also mean a
 * second spreadsheet library in a bundle that already carries two.
 * ------------------------------------------------------------------------- */

const EMU_PER_PT = 12700;
const EMU_PER_PX = 9525;

/**
 * A column's width in pixels, from the `wch` character width xlsx stores.
 *
 * Excel sizes columns in "characters" of the Normal font, which for the default
 * Calibri 11 is 7 pixels wide plus 5 pixels of cell padding. Approximate, and
 * the approximation Excel itself documents — exact enough to centre a logo,
 * which is why the anchor below is forgiving about where it lands.
 */
const colWidthPx = (col) => Math.round((col && col.wch ? col.wch : 8) * 7 + 5);

/**
 * Turns an absolute x in pixels into the {col, colOff} pair a drawing anchor
 * needs: which column the point falls in, and how far into it.
 */
const anchorAtX = (cols, xPx) => {
    let left = 0;
    for (let c = 0; c < cols.length; c += 1) {
        const w = colWidthPx(cols[c]);
        if (xPx < left + w) {
            return { col: c, colOff: Math.max(0, Math.round((xPx - left) * EMU_PER_PX)) };
        }
        left += w;
    }
    return { col: Math.max(0, cols.length - 1), colOff: 0 };
};

/** Inserts `child` directly before the closing tag of `parent`. */
const insertBefore = (xml, closingTag, child) => xml.replace(closingTag, child + closingTag);

const buildDrawing = ({ col, colOff, rowOff }, heightPt, widthPt) => {
    const cx = Math.round(widthPt * EMU_PER_PT);
    const cy = Math.round(heightPt * EMU_PER_PT);
    return (
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<xdr:wsDr xmlns:xdr="http://schemas.openxmlformats.org/drawingml/2006/spreadsheetDrawing"' +
        ' xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main">' +
        // oneCellAnchor: pinned to one cell at a fixed size, so the mark keeps
        // its proportions whatever the column widths turn out to be.
        '<xdr:oneCellAnchor>' +
        `<xdr:from><xdr:col>${col}</xdr:col><xdr:colOff>${colOff}</xdr:colOff>` +
        `<xdr:row>0</xdr:row><xdr:rowOff>${rowOff}</xdr:rowOff></xdr:from>` +
        `<xdr:ext cx="${cx}" cy="${cy}"/>` +
        '<xdr:pic><xdr:nvPicPr>' +
        '<xdr:cNvPr id="1" name="Contingent Fitness"/>' +
        '<xdr:cNvPicPr><a:picLocks noChangeAspect="1"/></xdr:cNvPicPr>' +
        '</xdr:nvPicPr>' +
        '<xdr:blipFill><a:blip xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" r:embed="rId1"/>' +
        '<a:stretch><a:fillRect/></a:stretch></xdr:blipFill>' +
        '<xdr:spPr><a:xfrm><a:off x="0" y="0"/>' +
        `<a:ext cx="${cx}" cy="${cy}"/></a:xfrm>` +
        '<a:prstGeom prst="rect"><a:avLst/></a:prstGeom></xdr:spPr>' +
        '</xdr:pic><xdr:clientData/></xdr:oneCellAnchor></xdr:wsDr>'
    );
};

/**
 * Returns the workbook bytes with the logo in the banner, or the bytes
 * untouched when anything at all is missing or unexpected.
 */
const withLogo = (workbookBytes, logo, { cols, heightPt, bannerHeightPt }) => {
    if (!logo || !logo.bytes || !logo.bytes.length) return workbookBytes;
    try {
        const files = unzipSync(new Uint8Array(workbookBytes));

        // The sheet is written as sheet1.xml by SheetJS, but look rather than
        // assume -- a wrong guess here produces a file Excel refuses to open.
        const sheetPath = Object.keys(files).find((f) => /^xl\/worksheets\/sheet1\.xml$/.test(f));
        const typesPath = '[Content_Types].xml';
        if (!sheetPath || !files[typesPath]) return workbookBytes;

        const aspect = (logo.width || 93) / (logo.height || 100);
        const widthPt = heightPt * aspect;

        /*
         * Centred across the banner, left to right.
         *
         * A drawing anchor is expressed as an offset INTO A COLUMN, not as an
         * absolute position, so the midpoint of the sheet has to be measured in
         * pixels first and then resolved back to whichever column it lands in.
         * The title sits left-aligned in the same merged band, so a centred mark
         * has the middle of the row to itself.
         */
        const sheetWidthPx = cols.reduce((sum, c) => sum + colWidthPx(c), 0);
        const logoWidthPx = (widthPt * EMU_PER_PT) / EMU_PER_PX;
        const from = anchorAtX(cols, Math.max(0, sheetWidthPx / 2 - logoWidthPx / 2));

        // Centred vertically in the banner too, so it does not sit on the rule
        // under it. Clamped at zero: a logo taller than its banner starts at the
        // top rather than at a negative offset, which Excel rejects.
        const rowOff = Math.max(0, Math.round(((bannerHeightPt - heightPt) / 2) * EMU_PER_PT));

        files['xl/media/image1.png'] = logo.bytes;
        files['xl/drawings/drawing1.xml'] = strToU8(
            buildDrawing({ ...from, rowOff }, heightPt, widthPt)
        );
        files['xl/drawings/_rels/drawing1.xml.rels'] = strToU8(
            '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
            '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
            '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/image"' +
            ' Target="../media/image1.png"/></Relationships>'
        );
        files['xl/worksheets/_rels/sheet1.xml.rels'] = strToU8(
            '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
            '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
            '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/drawing"' +
            ' Target="../drawings/drawing1.xml"/></Relationships>'
        );

        // <drawing> must be the LAST element of the worksheet; Excel validates
        // the child order and rejects the file outright if it is not.
        let sheetXml = new TextDecoder().decode(files[sheetPath]);
        if (sheetXml.indexOf('<drawing ') === -1) {
            sheetXml = insertBefore(sheetXml, '</worksheet>', '<drawing r:id="rId1"/>');
        }
        // SheetJS omits the relationship namespace when a sheet has no rels.
        if (sheetXml.indexOf('xmlns:r=') === -1) {
            sheetXml = sheetXml.replace(
                '<worksheet ',
                '<worksheet xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" '
            );
        }
        files[sheetPath] = strToU8(sheetXml);

        let types = new TextDecoder().decode(files[typesPath]);
        if (types.indexOf('Extension="png"') === -1) {
            types = types.replace(
                '<Types ',
                '<Types '
            ).replace(
                /(<Types[^>]*>)/,
                '$1<Default Extension="png" ContentType="image/png"/>'
            );
        }
        if (types.indexOf('drawing1.xml') === -1) {
            types = insertBefore(
                types,
                '</Types>',
                '<Override PartName="/xl/drawings/drawing1.xml"' +
                ' ContentType="application/vnd.openxmlformats-officedocument.drawing+xml"/>'
            );
        }
        files[typesPath] = strToU8(types);

        return zipSync(files);
    } catch (err) {
        // A workbook without its logo is a small loss; a corrupt one is not.
        console.log('Could not place the logo in the workbook:', err && err.message);
        return workbookBytes;
    }
};

/** Creates the cell if it does not exist, so a style can be hung on it. */
const setCell = (sheet, r, c, value, style) => {
    const address = XLSX.utils.encode_cell({ r, c });
    const existing = sheet[address];
    const v = value === undefined ? (existing ? existing.v : '') : value;
    const cell = existing || {};
    cell.v = v;
    // A blank is written as an empty string rather than left untyped: an untyped
    // cell is dropped on write and takes its fill with it, which punches holes
    // in every banner and header band.
    cell.t = typeof v === 'number' ? 'n' : 's';
    if (style) cell.s = { ...(cell.s || {}), ...style };
    sheet[address] = cell;
    return cell;
};

/** Styles a whole row across `width` columns, creating blanks as needed. */
const styleRow = (sheet, r, width, style) => {
    for (let c = 0; c < width; c += 1) setCell(sheet, r, c, undefined, style);
};

export const downloadExcelSheet = async ({
    title,
    subtitle,
    rows = [],
    sectionRows,
    headerRows,
    labelRows,
    highlightRows = [],
    totalRows = [],
    boldColumns = [],
    noteRows = [],
    numberFormats = {},
    boldRows = [],
    boldLabelRows = [],
    fileName = 'report.xlsx',
    sheetName = 'Report',
}) => {
    const sections = sectionRows || [];
    const headers = headerRows || boldRows;
    const labels = labelRows || boldLabelRows;

    const width = Math.max(
        1,
        rows.reduce((max, row) => Math.max(max, row?.length || 0), 0)
    );

    /*
     * The banner is prepended here rather than by every caller, so no report can
     * end up without one. Everything downstream — row indexes the caller gave
     * us, merges, widths — has to shift by its height.
     */
    const bannerHeight = title ? (subtitle ? 2 : 1) : 0;
    const body = title ? [[title], ...(subtitle ? [[subtitle]] : []), ...rows] : rows;

    const sheet = XLSX.utils.aoa_to_sheet(body);
    const merges = [];
    const rowHeights = [];
    const shift = (i) => i + bannerHeight;

    // ---- banner ------------------------------------------------------------
    if (title) {
        merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: width - 1 } });
        styleRow(sheet, 0, width, {
            fill: { fgColor: { rgb: NAVY } },
            font: { bold: true, sz: 15, color: { rgb: 'FFFFFF' } },
            alignment: { vertical: 'center', horizontal: 'left', indent: 1 },
        });
        rowHeights[0] = { hpt: 30 };

        if (subtitle) {
            merges.push({ s: { r: 1, c: 0 }, e: { r: 1, c: width - 1 } });
            styleRow(sheet, 1, width, {
                fill: { fgColor: { rgb: NAVY } },
                font: { sz: 10, color: { rgb: 'D6DDEA' } },
                alignment: { vertical: 'center', horizontal: 'left', indent: 1 },
            });
            rowHeights[1] = { hpt: 18 };
        }
    }

    // ---- section bars ------------------------------------------------------
    sections.forEach((i) => {
        const r = shift(i);
        merges.push({ s: { r, c: 0 }, e: { r, c: width - 1 } });
        styleRow(sheet, r, width, {
            fill: { fgColor: { rgb: SECTION } },
            font: { bold: true, sz: 10, color: { rgb: 'FFFFFF' } },
            alignment: { vertical: 'center', horizontal: 'left', indent: 1 },
        });
        rowHeights[r] = { hpt: 20 };
    });

    // ---- table header bands ------------------------------------------------
    headers.forEach((i) => {
        const r = shift(i);
        const cells = rows[i]?.length || width;
        for (let c = 0; c < cells; c += 1) {
            setCell(sheet, r, c, undefined, {
                fill: { fgColor: { rgb: HEAD } },
                font: { bold: true, sz: 10, color: { rgb: INK } },
                alignment: { vertical: 'center', wrapText: true },
                border: boxed,
            });
        }
        rowHeights[r] = { hpt: 20 };
    });

    // ---- label / value pairs -----------------------------------------------
    labels.forEach((i) => {
        const r = shift(i);
        setCell(sheet, r, 0, undefined, {
            fill: { fgColor: { rgb: HEAD } },
            font: { bold: true, sz: 10, color: { rgb: INK } },
            alignment: { vertical: 'top', wrapText: true },
            border: boxed,
        });
        const cells = rows[i]?.length || 1;
        for (let c = 1; c < cells; c += 1) {
            setCell(sheet, r, c, undefined, {
                alignment: { vertical: 'top', wrapText: true },
                border: boxed,
            });
        }
    });

    /* ---- headline figures --------------------------------------------------
       The one or two numbers a reader opens the file to find. Given the green
       fill and a larger bold face the client's own workbook uses for MONTHLY
       TOTAL and BLENDED RATE, so the eye lands on them before anything else. */
    highlightRows.forEach((i) => {
        const r = shift(i);
        setCell(sheet, r, 0, undefined, {
            fill: { fgColor: { rgb: HEAD } },
            font: { bold: true, sz: 11, color: { rgb: INK } },
            alignment: { vertical: 'center' },
            border: boxed,
        });
        const cells = Math.max(rows[i]?.length || 1, 2);
        for (let c = 1; c < cells; c += 1) {
            const value = rows[i]?.[c];
            setCell(sheet, r, c, undefined, {
                fill: { fgColor: { rgb: VALUE } },
                font: { bold: true, sz: 12, color: { rgb: INK } },
                alignment: {
                    vertical: 'center',
                    horizontal: typeof value === 'number' ? 'right' : 'left',
                },
                border: boxed,
                ...(numberFormats[c] && typeof value === 'number'
                    ? { numFmt: numberFormats[c] }
                    : {}),
            });
        }
        rowHeights[r] = { hpt: 22 };
    });

    // ---- totals inside a table ---------------------------------------------
    totalRows.forEach((i) => {
        const r = shift(i);
        const cells = rows[i]?.length || width;
        for (let c = 0; c < cells; c += 1) {
            const value = rows[i]?.[c];
            setCell(sheet, r, c, undefined, {
                fill: { fgColor: { rgb: VALUE } },
                font: { bold: true, sz: 10, color: { rgb: INK } },
                alignment: {
                    vertical: 'center',
                    horizontal: typeof value === 'number' ? 'right' : 'left',
                },
                border: totalled,
                ...(numberFormats[c] && typeof value === 'number'
                    ? { numFmt: numberFormats[c] }
                    : {}),
            });
        }
    });

    // ---- note blocks -------------------------------------------------------
    noteRows.forEach((i) => {
        const r = shift(i);
        merges.push({ s: { r, c: 0 }, e: { r, c: width - 1 } });
        styleRow(sheet, r, width, {
            fill: { fgColor: { rgb: NOTE_BG } },
            font: { sz: 9, italic: true, color: { rgb: INK } },
            alignment: { vertical: 'center', horizontal: 'left', wrapText: true, indent: 1 },
        });
    });

    // ---- plain data rows ---------------------------------------------------
    const special = new Set([
        ...sections, ...headers, ...labels, ...highlightRows, ...totalRows, ...noteRows,
    ]);
    const emphasised = new Set(boldColumns);
    rows.forEach((row, i) => {
        if (special.has(i) || !row || row.length === 0) return;
        const r = shift(i);
        for (let c = 0; c < row.length; c += 1) {
            const value = row[c];
            if (value === undefined || value === null || value === '') continue;
            setCell(sheet, r, c, undefined, {
                // The money column in a working table reads as the answer; the
                // rest of the row is how it was arrived at.
                font: { sz: 10, bold: emphasised.has(c) },
                alignment: {
                    vertical: 'center',
                    horizontal: typeof value === 'number' ? 'right' : 'left',
                    wrapText: true,
                },
                border: boxed,
                ...(numberFormats[c] && typeof value === 'number'
                    ? { numFmt: numberFormats[c] }
                    : {}),
            });
        }
    });

    sheet['!merges'] = merges;
    if (rowHeights.length) sheet['!rows'] = rowHeights;

    /*
     * Widths come from the DATA rows only. A banner or a note is merged across
     * the whole sheet, so letting its length vote would blow column A out to the
     * width of a paragraph and push every figure off the screen.
     */
    const measured = new Set([...sections, ...noteRows]);
    sheet['!cols'] = Array.from({ length: width }, (_, c) => {
        const longest = rows.reduce((max, row, i) => {
            if (measured.has(i)) return max;
            const value = row?.[c];
            if (value === undefined || value === null) return max;
            // A wrapped cell does not need to fit on one line.
            return Math.max(max, Math.min(String(value).length, 48));
        }, 0);
        return { wch: Math.min(Math.max(longest + 2, 12), 52) };
    });

    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, sheetName);

    /*
     * Written to bytes rather than straight to a file, because the logo has to
     * go into the zip before the browser is handed anything. `XLSX.writeFile`
     * would have downloaded it already.
     */
    const bytes = XLSX.write(book, { bookType: 'xlsx', type: 'array' });
    const logo = title ? await loadLogoPngBytes(4) : null;
    const finalBytes = title
        ? withLogo(bytes, logo, {
            // The column widths settled above are what the centring is measured
            // against — they decide how wide the banner actually is.
            cols: sheet['!cols'],
            heightPt: subtitle ? 34 : 20,
            bannerHeightPt: subtitle ? 48 : 30,
        })
        : bytes;

    const blob = new Blob([finalBytes], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Revoked on the next tick: Safari has not finished reading the blob when
    // click() returns, and revoking synchronously gives an empty file.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export default downloadExcelSheet;
